import React, { useState } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const DissertationServices: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const otherServices = [
    { title: "Dissertation", route: "/dissertation-writing-services" },
    { title: "Coursework", route: "/coursework-writing" },
    { title: "Data Analytics", route: "/data-analysis-services" },
    { title: "Publication", route: "/publication-services" },
    { title: "Literature review", route: "/literature-review-writing" },
    { title: "Manuscript", route: "/manuscript-preparation" },
    { title: "Research methodology", route: "/research-methodology-services" },
  ];

  const data = {
    icon: "ds5.webp",
    title: "Project Report Writing Services",
    image: "ds5.webp",
    description:
      "Get professional project report writing services that ensure clear, detailed, and impactful reports that meet academic standards.",
    additionalInfo: [
      {
        title: "Report Structuring",
        icon1: "",
        description:
          "Our expert team offers professional report structuring services, ensuring your project report is well-organized, coherent, and follows the required academic formatting. We help you break down complex ideas and data into clear sections, making your report easy to understand.",
        cards: [
          {
            title: "Report Organization Techniques",
            description:
              "Understanding how to organize a project report is key to presenting information effectively. This section provides insights into structuring reports with proper headings, subheadings, and logical flow to guide readers through your project’s objectives, methodology, and findings.",
          },
          {
            title: "Formatting Guidelines",
            description:
              "Formatting is crucial for readability and professionalism. We cover the best practices for formatting project reports, including font sizes, margins, spacing, and the correct use of headers, footers, and page numbers, ensuring your report meets academic or industry-specific standards.",
          },
          {
            title: "Abstract and Executive Summary",
            description:
              "The abstract and executive summary are vital components of a project report. This section teaches how to write concise summaries that capture the essence of the report, highlighting key findings, objectives, and recommendations.",
          },
          {
            title: "Sections and Subsections",
            description:
              "Effective use of sections and subsections helps to break down complex information. This section explains how to organize the report’s content into easily digestible parts, covering the introduction, literature review, methodology, results, and conclusion.",
          },
        ],
      },
      {
        title: "Effective Literature Review Writing for Academic Success",
        icon1: "",
        description:
          "Our Effective Literature Review Writing service helps you build a comprehensive and insightful review of the current research landscape. We help you identify key studies, critically analyze their findings, and organize your review in a way that supports your project goals and academic success.",
        cards: [
          {
            title: "Locating Reliable Academic Sources",
            description:
              "Find and use reliable academic sources to build a solid foundation for your literature review, ensuring that your research is credible and authoritative.",
          },
          {
            title: "Analyzing and Synthesizing Literature",
            description:
              "Learn how to synthesize existing research, identify key themes, and critique methodologies used in previous studies to build a strong framework for your project.",
          },
          {
            title: "Research Gaps Identification",
            description:
              "Identify gaps in existing research to position your work as a valuable contribution to the field, demonstrating the relevance and necessity of your research question.",
          },
          {
            title: "Citation and Referencing Mastery",
            description:
              "Ensure that your citations are impeccable and formatted correctly using citation styles such as APA, MLA, or Chicago. We guide you in maintaining academic integrity and avoiding plagiarism.",
          },
        ],
      },

      {
        title: "Comprehensive Research Methods and Writing Support for Reports",
        icon1: "",
        description:
          "Our Comprehensive Research Methods and Writing Support service ensures that your project report is backed by sound research methodologies, clear writing, and effective data presentation. We offer step-by-step assistance with designing, writing, and analyzing your research project.",
        cards: [
          {
            title: "Designing a Research Methodology",
            description:
              "We guide you in designing a robust research methodology, helping you choose the right approach, tools, and techniques that align with your research goals.",
          },
          {
            title: "Analyzing Data for Research Reports",
            description:
              "Our experts assist you in analyzing complex data sets, ensuring that your research findings are interpreted and presented with accuracy and clarity.",
          },
          {
            title: "Writing Clear and Concise Reports",
            description:
              "We teach you the art of writing clear and concise research reports that convey your findings in an organized and professional manner.",
          },
          {
            title: "Integrating Research with Academic Writing Standards",
            description:
              "We help you integrate your research with academic writing standards, ensuring your report is credible, well-structured, and aligned with your academic field.",
          },
        ],
      },

      {
        title: "Project Report Editing & Proofreading",
        icon1: "",
        description:
          "Our editing and proofreading services ensure that your project report is free of errors and adheres to academic standards. We meticulously check for grammar, spelling, punctuation, and overall readability to ensure the highest quality.",
        cards: [
          {
            title: "Grammar and Syntax Check",
            description:
              "Proper grammar and syntax are essential for a professional project report. This section covers common grammar issues and provides tips on how to improve sentence structure, ensuring clarity and precision in your writing.",
          },
          {
            title: "Spelling and Punctuation",
            description:
              "Spelling and punctuation errors can undermine the quality of your report. This section emphasizes the importance of spelling accuracy and proper punctuation usage, offering tips on how to avoid common mistakes.",
          },
          {
            title: "Coherence and Flow",
            description:
              "A well-written report must flow logically from one section to the next. This section explains how to improve coherence in your writing, ensuring that your arguments are clearly presented and logically connected.",
          },
          {
            title: "Final Proofreading",
            description:
              "Final proofreading is crucial before submission. This section discusses the steps involved in a thorough proofreading process, checking for any overlooked errors or inconsistencies that may affect the report’s quality.",
          },
        ],
      },
      {
        title: "Project Report Formatting for Submission",
        icon1: "",
        description:
          "We help format your project report to meet the specific guidelines required for submission. Whether it's an academic report, business proposal, or research paper, our formatting service ensures your report meets all professional standards.",
        cards: [
          {
            title: "Adhering to Submission Guidelines",
            description:
              "Different institutions and organizations may have unique formatting requirements. This section explains how to carefully follow submission guidelines regarding font size, spacing, margin width, and header/footer placement to meet specific standards.",
          },
          {
            title: "Formatting Tables and Figures",
            description:
              "Proper formatting of tables, figures, and charts is key for readability. This section offers best practices for presenting visuals in a professional, consistent manner, ensuring your data is clearly displayed.",
          },
          {
            title: "Creating Professional Titles and Headers",
            description:
              "Professional-looking titles and headers make a project report stand out. This section guides you on how to create effective titles, section headers, and subheadings that capture the essence of your content and make navigation easier.",
          },
          {
            title: "Final Layout and Design",
            description:
              "The final layout and design of your report contribute to its overall appeal. This section discusses the importance of a clean, organized layout that enhances readability and leaves a lasting impression on the reader.",
          },
        ],
      },
    ],
  };

  const handleServiceClick = (route: string) => {
    window.location.href = route;
  };

  const handleHeadingClick = (index: number) => {
    setActiveIndex(activeIndex === index ? 0 : index);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Grid
        container
        spacing={0}
        sx={{
          backgroundColor: "#172554",
          padding: { sm: 0, lg: 3 },
          display: "flex",
          flexDirection: "row-reverse",
        }}
        className="component"
      >
        {/* Left Side - Other Services List (Desktop) and Expandable Headings (Mobile) */}
        <Grid item xs={12} md={3}>
          {/* Desktop View */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <List>
              {data.additionalInfo.map((info, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleHeadingClick(index)}
                  selected={activeIndex === index}
                  sx={{ color: "white" }}
                >
                  <ListItemText primary={info.title} />
                </ListItem>
              ))}
              <Typography
                sx={{ textAlign: "center", color: "white", fontWeight: "bold" }}
              >
                Our Other Services
              </Typography>
              {otherServices.map((service, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleServiceClick(service.route)}
                  sx={{ color: "white" }}
                >
                  <ListItemText primary={service.title} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Mobile View - Expandable Headings */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {data.additionalInfo.map((info, index) => (
              <Box key={index}>
                <ListItem
                  button
                  onClick={() => handleHeadingClick(index)}
                  sx={{
                    color: "white",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <ListItemText primary={info.title} />
                  <IconButton sx={{ color: "white" }}>
                    {activeIndex === index ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </ListItem>
                <Collapse
                  in={activeIndex === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      backgroundColor: "#f3f4f6",
                      color: "black",
                      padding: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                      {info.description}
                    </Typography>
                    {info.cards.map((card, cardIndex) => (
                      <Card
                        key={cardIndex}
                        sx={{
                          marginBottom: 2,
                          backgroundColor: "#172554",
                          color: "white",
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6">
                            <i
                              className="fa-solid fa-circle-check"
                              style={{ color: "#f97316", paddingRight: "10px" }}
                            ></i>
                            {card.title}
                          </Typography>
                          <Typography variant="body2">
                            {card.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Right Side - Dissertation Content (Desktop Only) */}
        <Grid item md={9} sx={{ display: { xs: "none", md: "block" } }}>
          <Paper
            elevation={3}
            sx={{
              padding: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#f3f4f6",
              boxShadow: "none",
              borderRadius: "0px",
            }}
          >
            {activeIndex !== null && (
              <>
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {data.additionalInfo[activeIndex].title}
                </Typography>
                <Box sx={{ marginBottom: 2 }}></Box>
                <Typography sx={{ marginBottom: 4, textAlign: "center" }}>
                  {data.additionalInfo[activeIndex].description}
                </Typography>

                <Grid
                  container
                  direction="column"
                  spacing={2}
                  sx={{
                    backgroundColor: "#172554",
                    color: "white",
                    borderRadius: "4px",
                    maxWidth: { lg: "75%", xs: "100%" },
                  }}
                >
                  {data.additionalInfo[activeIndex].cards.map((card, index) => (
                    <Grid item key={index}>
                      <Card
                        sx={{
                          boxShadow: "none",
                          backgroundColor: "transparent",
                          color: "white",
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" color={"white"}>
                            <i
                              className="fa-solid fa-circle-check"
                              style={{ color: "#f97316", paddingRight: "10px" }}
                            ></i>
                            {card.title}
                          </Typography>
                          <Typography variant="body2" textAlign={"center"}>
                            {card.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DissertationServices;
